import React, { useContext } from 'react';
import styled from 'styled-components';
import { useI18n } from '@oneaudi/i18n-context';
import type { TrackingData } from '../../feature-service-types';
import { createPreloadAction, createSetActiveItemAction } from '../../actions/header-actions';
import type { AudiHeaderSearchButtonProps } from '../../interfaces/header-components.interfaces';
import type { HeaderStyledSearchButtonProps } from '../../interfaces/header-styled-components.interfaces';
import { EventAction } from '../../utils/tracking';
import { ServiceContext } from '../../context/ServiceContext';
import { NAVIGATION_CLASSNAME, NAVIGATION_MOBILE_CLASSNAME } from '../../utils/globals';

const HeaderStyledSearchButton = styled.div<HeaderStyledSearchButtonProps>`
  display: ${({ isOpen }): string => (isOpen ? 'block' : 'none')};
  margin: var(--one-header-space-l) 0;

  ${({ isSmallView, theme: { breakpoints } }) =>
    !isSmallView &&
    `@media screen and (min-width: ${breakpoints.l}px) {
      display: block;
      margin-right: var(--one-header-space-s);
    }`}

  & button {
    background-color: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    padding: 0;
  }

  & button:focus {
    outline: none;
  }

  & button:focus-visible {
    outline: auto;
  }

  & button svg {
    color: var(--one-header-color-black);
    height: 24px;
    width: 24px;
  }
`;

const AudiHeaderSearchButton: React.FC<AudiHeaderSearchButtonProps> = (props) => {
  const { headerStateService, trackingService } = useContext(ServiceContext);
  const { headerDispatch, headerNavigationItemsAmount, isOpen } = props;

  let isSmallView = true;
  headerStateService.registerCallback((headerStateRemote) => {
    isSmallView = headerStateRemote.isMobileView;
  });

  const SearchAriaLabel = useI18n({
    defaultMessage: 'Search',
    id: 'nemo.ui.common.search-button',
  });

  const handleSearchAccess: (event: React.MouseEvent<HTMLButtonElement>) => void = (event) => {
    event.preventDefault();
    // event tracking
    const SearchAccessEvent: TrackingData = {
      event: {
        eventInfo: {
          eventAction: EventAction.NAVIGATION,
          eventName: 'one header - search access',
        },
        attributes: {
          componentName: EventAction.COMPONENT_NAME,
          label: SearchAriaLabel,
          targetURL: '',
          clickID: '',
          elementName: 'icon',
          value: '',
          pos: `${headerNavigationItemsAmount + 1}`,
        },
      },
    };
    trackingService?.track(SearchAccessEvent);
    // dispatch status update
    headerDispatch(
      createSetActiveItemAction({
        anchor: null,
        headerStateService,
        index: -1,
        showSearch: true,
        showDealer: false,
        showDealerSearch: false,
        showLoginFlyout: false,
        showWistListFlyout: false,
        openDealerDetails: false,
      }),
    );
  };

  const handleFocusEvent: (
    event:
      | React.FocusEvent<HTMLButtonElement>
      | React.MouseEvent<HTMLButtonElement>
      | React.TouchEvent<HTMLButtonElement>,
  ) => void = () => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    headerDispatch(createPreloadAction());
  };

  return (
    <HeaderStyledSearchButton
      headerNavigationItemsAmount={headerNavigationItemsAmount}
      isSmallView={isSmallView}
      isOpen={isOpen}
    >
      <button
        aria-label={SearchAriaLabel}
        title={SearchAriaLabel}
        onClick={handleSearchAccess}
        onFocus={handleFocusEvent}
        onMouseEnter={handleFocusEvent}
        onTouchStart={handleFocusEvent}
        type="button"
        className={`${NAVIGATION_CLASSNAME} ${NAVIGATION_MOBILE_CLASSNAME}`}
      >
        <svg height="24" width="24" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M22 22l-8.3-8.3L22 22zM15.5 9c.1 3.5-2.6 6.4-6.1 6.5H9c-3.6 0-6.5-2.9-6.5-6.5S5.4 2.5 9 2.5s6.5 2.9 6.5 6.5c0 0 0 0 0 0z"
            fill="none"
            stroke="currentColor"
          />
        </svg>
      </button>
    </HeaderStyledSearchButton>
  );
};

export default AudiHeaderSearchButton;
